import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Default theme values from backend settings
const defaultAppTheme = {
  // Login page
  login_logo: '',
  login_background: '',
  login_bg_color: '#f8fafc',
  login_card_bg: '#ffffff',
  login_welcome_text: 'Welcome to LinkHub',
  login_subtitle: 'Sign in to your account',
  
  // Admin panel
  admin_logo: '',
  admin_logo_dark: '',
  favicon: '',
  
  // Colors
  primary_color: '#154734',
  secondary_color: '#ba0c2f',
  sidebar_bg: '#1f2937',
  sidebar_text: '#ffffff',
  
  // Typography
  heading_font: 'Montserrat',
  body_font: 'Inter',
  
  // Options
  enable_dark_mode: true,
  compact_mode: false,
  show_branding: true,
  custom_css: '',
};

const AppThemeContext = createContext({
  appTheme: defaultAppTheme,
  loading: true,
  refreshAppTheme: () => {},
});

export function AppThemeProvider({ children }) {
  const [appTheme, setAppTheme] = useState(defaultAppTheme);
  const [loading, setLoading] = useState(true);

  const fetchAppTheme = useCallback(async () => {
    try {
      // Try public theme endpoint first (for login page)
      let response = await fetch(`${API_URL}/api/v1/settings/public/theme`);
      
      if (!response.ok) {
        // Fallback to main settings endpoint (requires auth)
        const token = localStorage.getItem('token');
        if (token) {
          response = await fetch(`${API_URL}/api/v1/settings`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
        }
      }
      
      if (response && response.ok) {
        const data = await response.json();
        if (data.success) {
          // Handle both public/theme endpoint and full settings endpoint
          const themeData = data.data?.theme || data.data;
          if (themeData) {
            setAppTheme({ ...defaultAppTheme, ...themeData });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching app theme:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAppTheme();
  }, [fetchAppTheme]);

  // Apply CSS variables to document root
  useEffect(() => {
    const root = document.documentElement;
    
    // Apply color variables
    root.style.setProperty('--theme-primary', appTheme.primary_color);
    root.style.setProperty('--theme-secondary', appTheme.secondary_color);
    root.style.setProperty('--theme-sidebar-bg', appTheme.sidebar_bg);
    root.style.setProperty('--theme-sidebar-text', appTheme.sidebar_text);
    root.style.setProperty('--theme-login-bg', appTheme.login_bg_color);
    root.style.setProperty('--theme-login-card-bg', appTheme.login_card_bg);
    
    // Apply fonts
    root.style.setProperty('--theme-heading-font', appTheme.heading_font);
    root.style.setProperty('--theme-body-font', appTheme.body_font);
    
    // Apply favicon - use favicon if set, otherwise fallback to admin_logo
    const faviconUrl = appTheme.favicon || appTheme.admin_logo || appTheme.login_logo;
    if (faviconUrl) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = faviconUrl;
    }
    
    // Apply custom CSS if set
    if (appTheme.custom_css) {
      let style = document.getElementById('app-theme-custom-css');
      if (!style) {
        style = document.createElement('style');
        style.id = 'app-theme-custom-css';
        document.head.appendChild(style);
      }
      style.textContent = appTheme.custom_css;
    }
  }, [appTheme]);

  const value = {
    appTheme,
    loading,
    refreshAppTheme: fetchAppTheme,
  };

  return (
    <AppThemeContext.Provider value={value}>
      {children}
    </AppThemeContext.Provider>
  );
}

export function useAppTheme() {
  const context = useContext(AppThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within an AppThemeProvider');
  }
  return context;
}

export default AppThemeContext;
